import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, Globe, Package, ArrowLeft } from "lucide-react";
import { Reveal, Overline } from "@/components/motion";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import QuoteDialog from "@/components/QuoteDialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PRODUCTS, PRODUCTS_BY_SLUG, SPEC_ORDER } from "@/data/products";
import { productSchema, faqSchema, breadcrumbSchema } from "@/lib/seo";

const PRODUCT_SEO_LINKS = {
  "machine-clean-millet": { label: "Explore millet export from India", to: "/millet-export" },
  "bajra-pearl-millet": { label: "Explore pearl millet export from India", to: "/millet-export/pearl-millet-bajra" },
};

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCTS_BY_SLUG[slug];

  if (!product) {
    return (
      <div>
        <Seo title="Product Not Found" description="The requested product could not be found." path={`/products/${slug}`} noindex />
        <section className="pt-40 pb-32 bg-brand-cream text-center">
          <h1 className="font-display text-4xl text-brand-green">Product not found</h1>
          <Link to="/products" className="inline-flex items-center gap-2 mt-6 text-brand-green font-semibold">
            <ArrowLeft size={16} /> Back to Products
          </Link>
        </section>
      </div>
    );
  }

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Products", path: "/products" },
    { name: product.name, path: `/products/${product.slug}` },
  ];

  const related = PRODUCTS.filter((p) => p.slug !== product.slug);

  return (
    <div>
      <Seo
        title={product.seoTitle}
        description={product.seoDescription}
        path={`/products/${product.slug}`}
        image={product.image}
        type="product"
        jsonLd={[
          productSchema(product),
          breadcrumbSchema(crumbs),
          faqSchema(product.faqs),
        ]}
      />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 bg-brand-green-dark">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Breadcrumbs items={crumbs} className="mb-6" /></Reveal>
          <Reveal delay={0.05}>
            <span className="text-[11px] uppercase tracking-[0.2em] text-brand-gold-light font-semibold">{product.tagline}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-brand-cream leading-none mt-3">
              {product.name}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-lg text-brand-cream/75 max-w-2xl">{product.short}</p>
          </Reveal>
        </div>
      </section>

      {/* Main */}
      <section className="bg-brand-cream py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <div className="overflow-hidden rounded-sm">
                    <img
                    src={product.image}
                    alt={product.imageAlt || product.name}
                      fetchPriority="high"
                      decoding="async"
                      sizes="(min-width: 1024px) 50vw, 100vw"
                    width="720"
                    height="900"
                    className="w-full aspect-[4/5] object-cover"
                  />
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <QuoteDialog product={product.name}>
                  <button data-testid={`detail-quote-${product.slug}`} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-green text-white px-8 py-4 rounded-full font-medium hover:bg-brand-green-dark transition-colors">
                    Request a Quote <ArrowRight size={16} />
                  </button>
                </QuoteDialog>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="font-display text-3xl font-medium text-brand-green">Overview</h2>
              <p className="text-lg text-brand-charcoal leading-relaxed mt-4">{product.description}</p>
              {PRODUCT_SEO_LINKS[product.slug] && (
                <Link to={PRODUCT_SEO_LINKS[product.slug].to} className="inline-flex items-center gap-2 text-sm text-brand-green font-semibold mt-5 hover:text-brand-gold-dark transition-colors">
                  {PRODUCT_SEO_LINKS[product.slug].label} <ArrowRight size={14} />
                </Link>
              )}
            </Reveal>

            {/* Applications */}
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl font-medium text-brand-green mt-12">Applications</h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                {product.applications.map((a) => (
                  <li key={a} className="flex items-start gap-2.5 text-sm text-brand-charcoal">
                    <Check size={16} className="text-brand-green mt-0.5 shrink-0" aria-hidden="true" /> {a}
                  </li>
                ))}
              </ul>
            </Reveal>

            {/* Specifications */}
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl font-medium text-brand-green mt-12">Specifications</h2>
              <table className="w-full text-left border-collapse mt-5" data-testid={`spec-table-${product.slug}`}>
                <caption className="sr-only">{product.name} technical specifications</caption>
                <tbody>
                  {SPEC_ORDER.map((key) => (
                    <tr key={key} className="border-b border-brand-line">
                      <th scope="row" className="py-3.5 text-sm font-medium text-brand-muted w-1/2">{key}</th>
                      <td className="py-3.5 text-sm text-brand-charcoal tabular-nums text-right font-medium">{product.specs[key]}</td>
                    </tr>
                  ))}
                  <tr className="border-b border-brand-line">
                    <th scope="row" className="py-3.5 text-sm font-medium text-brand-muted">Packaging Options</th>
                    <td className="py-3.5 text-sm text-brand-charcoal text-right">10 kg / 25 kg / 50 kg &amp; others</td>
                  </tr>
                </tbody>
              </table>
            </Reveal>

            {/* Packaging & Grades */}
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl font-medium text-brand-green mt-12 flex items-center gap-3">
                <Package size={22} className="text-brand-gold-dark" aria-hidden="true" /> Packaging &amp; Grades
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                {product.packaging.map((a) => (
                  <li key={a} className="flex items-start gap-2.5 text-sm text-brand-charcoal">
                    <Check size={16} className="text-brand-green mt-0.5 shrink-0" aria-hidden="true" /> {a}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2 mt-4">
                {product.grades.map((g) => (
                  <span key={g} className="text-xs px-3 py-1 rounded-full border border-brand-gold/50 text-brand-gold-dark">{g}</span>
                ))}
              </div>
            </Reveal>

            {/* Export Markets */}
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl font-medium text-brand-green mt-12 flex items-center gap-3">
                <Globe size={22} className="text-brand-gold-dark" aria-hidden="true" /> Export Markets
              </h2>
              <div className="flex flex-wrap gap-2 mt-5">
                {product.exportMarkets.map((m) => (
                  <span key={m} className="text-sm px-4 py-1.5 rounded-full bg-brand-green/10 text-brand-green">{m}</span>
                ))}
              </div>
              <p className="text-xs text-brand-muted mt-4 italic">Minimum Order Quantity: {product.specs["Minimum Order Quantity"]}. Specifications are indicative and can be tailored to buyer requirements.</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-brand-line py-16 md:py-24" data-testid="product-faq">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <Reveal><Overline className="text-brand-gold-dark mb-5">FAQ</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl sm:text-5xl font-medium text-brand-green tracking-tight">
              {product.name} — Frequently Asked Questions
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10">
              {product.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-brand-line">
                  <AccordionTrigger className="text-left font-display text-xl text-brand-green hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-brand-muted text-base leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Related */}
      <section className="bg-brand-cream py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-display text-3xl font-medium text-brand-green">Other Products</h2>
            <Link to="/products" className="inline-flex items-center gap-2 text-brand-green font-semibold hover:gap-3 transition-all">
              All products <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="group bg-white border border-brand-line hover:border-brand-gold transition-colors" data-testid={`related-${p.slug}`}>
                <div className="overflow-hidden aspect-[16/10]">
                  <img src={p.image} alt={p.imageAlt || p.name} loading="lazy" decoding="async" sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <h3 className="font-display text-xl font-medium text-brand-green">{p.name}</h3>
                  <span className="inline-flex items-center gap-1.5 text-sm text-brand-gold-dark mt-2 group-hover:gap-2.5 transition-all">View details <ArrowRight size={13} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
